import cadviewer from "cadviewer";
import { motion, useDragControls } from "motion/react";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { X, MapPin } from "lucide-react";
import {
  useAIChatSelector,
} from "../AIChatSelector/AIChatSelector";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import FieldConfigModal from "../FieldConfigModal/FieldConfigModal";
import RatingModal from "../RatingModal/RatingModal";
import { CadviewerContext } from "../../../../context/Cadviewer.Context";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";
import useConfig from "../../../../demos/hooks/useConfig";
import { highlight_green, highlight_blue } from "../../utils/aiChatUtils";
import { submitAIRating, submitAnalyticsRating } from "../../utils/aiRatingService";
import { AIRatingPayload } from "../../types/aiRating";
import { getOrCreateEndUserId } from "../../utils/endUserId";
import EventSystem from "../../../../events/EventSystem";

// Import types
import { AIChatDrawerProps, SearchMode, DistanceType } from "./types/drawerTypes";

// Import hooks
import { useDrawerResize, useDrawerPosition, useSpaceSelection, usePopOutWindow } from "./hooks";

// Import stores
import { useSpaceSelectionStore } from "../../stores/spaceSelectionStore";

// Import utils
import { calculateDragConstraints, calculateDrawerStyle, createVoiceHandlers, createModalHandlers } from "./utils";
import {
  getSelectedSpacesData,
  calculateFinancialSummary,
  calculateOpportunityCost,
  calculateWingAnalysis,
  generateQuickActionMessage,
  getTenantContextForSpace,
  buildTenantRankingPrompt,
  buildTenantComparisonPrompt,
} from "./utils/quickActionCalculations";

// Import components
import {
  DrawerHeader,
  MessagesArea,
  InputArea,
  SelectedSpacesDrawer,
  QuickActionsMenu,
  FooterStatus,
  TopControlBar,
  WindowPortal,
} from "./components";

const AIChatDrawer: React.FC<AIChatDrawerProps> = ({ isOpen, onClose, isPinned = false, onTogglePin }) => {
  const {
    messages,
    isLoading,
    isSearching,
    error,
    thinkingText,
    thinkingStatuses,
    sendMessage,
    clearError,
    clearHistoryMessagesToSend,
    internalCADViewerAICallback,
    currentHighlight,
    clearCurrentHighlight,
    cancelQuery,
    addLocalMessage,
  } = useAIChatSelector();

  const { selectedPlan, selectedPlanDetail, multiSelectArray, setMultiSelectArray, plansInfo, enableDragSelectSpaces, selectedSpaceObjectID, setSelectedSpaceObjectID } =
    useContext(CadviewerContext);
  const { config } = useConfig();

  // Zustand store for space selection
  const { isSelectingSpaceForAI, spaceSelectedForAI, startSelectingSpaceForAI, setSpaceSelectedForAI, stopSelectingSpaceForAI, isDragSelectActive, startDragSelect, stopDragSelect } = useSpaceSelectionStore();

  // Watch for space selection when in selection mode
  // Only capture if we're in selection mode AND haven't already captured a space
  useEffect(() => {
    if (isSelectingSpaceForAI && selectedSpaceObjectID && !spaceSelectedForAI) {
      console.log('[AIChatDrawer] Space selected while in selection mode:', selectedSpaceObjectID);

      // Get plan and property info
      const planName = selectedPlanDetail?.planName || "Unknown Plan";
      const propertyName = selectedPlan?.propertyName || "Unknown Property";

      // Highlight the selected space in green on the CAD canvas
      // First clear any existing highlights
      cadviewer.cvjs_clearSpaceLayer();
      const normalizedSpaceID = selectedSpaceObjectID.replaceAll?.(".", "_").replaceAll?.(" ", "_") || selectedSpaceObjectID;
      cadviewer.cvjs_highlightSpace(normalizedSpaceID, highlight_green);

      // Store the selected space data (this also sets isSelectingSpaceForAI to false)
      setSpaceSelectedForAI({
        spaceNumber: selectedSpaceObjectID,
        planName,
        propertyName,
      });
    }
  }, [isSelectingSpaceForAI, selectedSpaceObjectID, selectedPlan, selectedPlanDetail, setSpaceSelectedForAI, spaceSelectedForAI]);

  // Watch for spacesSelected event to stop drag select mode (stops blinking)
  useEffect(() => {
    const handleSpacesSelected = () => {
      if (isDragSelectActive) {
        console.log('[AIChatDrawer] Spaces selected, stopping drag select mode');
        stopDragSelect();
      }
    };

    window.addEventListener('spacesSelected', handleSpacesSelected);
    return () => {
      window.removeEventListener('spacesSelected', handleSpacesSelected);
    };
  }, [isDragSelectActive, stopDragSelect]);

  //console.log({ plansInfo })

  // State
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  // The assistant answer the user is replying to (shown as a quote above the
  // input, WhatsApp-style). The conversation context is sent automatically.
  const [replyTarget, setReplyTarget] = useState<{ content: string } | null>(
    null
  );

  // Show the answer as a reply quote above the input and focus it so the user
  // can immediately type a follow-up question.
  const handleFollowUp = useCallback((replyContent: string) => {
    setReplyTarget(replyContent ? { content: replyContent } : null);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isFieldConfigModalOpen, setIsFieldConfigModalOpen] = useState(false);
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(false);
  const [isThinkingVisible, setIsThinkingVisible] = useState(true);
  const [distanceType, setDistanceType] = useState<DistanceType>("direct");
  const [searchMode, setSearchMode] = useState<SearchMode>("global");
  const [isVoiceMode, setIsVoiceMode] = useState(true);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const [isChartMode, setIsChartMode] = useState(false);
  const [showScopeMenu, setShowScopeMenu] = useState(false);
  const [showDistanceMenu, setShowDistanceMenu] = useState(false);
  const [showQuickActionsMenu, setShowQuickActionsMenu] = useState(false);
  const [showSamples, setShowSamples] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [ratingModalState, setRatingModalState] = useState<{
    isOpen: boolean;
    assistantMessage: any;
    userQuery: string;
    propertyName?: string;
    planName?: string;
    searchMode?: string;
  } | null>(null);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  // Custom hooks
  const { isPoppedOut, popOutWindow, togglePopOut, closePopOut } = usePopOutWindow();
  const { x, y } = useDrawerPosition(isOpen);
  const { drawerWidth, drawerHeight, drawerRef, onResizeStart } =
    useDrawerResize(480, 600, x, y);
  const {
    selectedSpacesChips,
    setSelectedSpacesChips,
    handleAddSelectedSpaces,
    handleRemoveSpaceChip,
    handleClearAllSpaceChips,
  } = useSpaceSelection(multiSelectArray, searchMode, setSearchMode, setMultiSelectArray);

  // Voice recorder hook
  const {
    isRecording: isVoiceRecording,
    error: voiceRecorderError,
    startRecording,
    stopRecording,
  } = useVoiceRecorder();

  // Dynamic drag constraints that automatically adapt to layout offsets
  const [dynamicConstraints, setDynamicConstraints] = useState({
    top: -window.innerHeight,
    bottom: window.innerHeight,
    left: -window.innerWidth,
    right: window.innerWidth,
  });

  useEffect(() => {
    if (!isOpen || !drawerRef.current) return;
    
    const updateBounds = () => {
      if (!drawerRef.current) return;
      const rect = drawerRef.current.getBoundingClientRect();
      const currentX = x.get();
      const currentY = y.get();
      
      // Calculate the true screen position of the drawer when x=0, y=0
      const layoutTop = rect.top - currentY;
      const layoutLeft = rect.left - currentX;
      
      // Calculate maximum allowed movement in each direction to keep a 16px buffer
      // from the edges of the browser window.
      const maxUp = -(layoutTop - 16);
      const maxDown = window.innerHeight - layoutTop - drawerHeight - 16;
      const maxLeftMove = -(layoutLeft - 16);
      const maxRightMove = window.innerWidth - layoutLeft - drawerWidth - 16;
      
      setDynamicConstraints({
        top: Math.min(maxUp, 0),
        bottom: Math.max(maxDown, 0),
        left: Math.min(maxLeftMove, 0),
        right: Math.max(maxRightMove, 0),
      });
    };

    const timer = setTimeout(updateBounds, 100);
    window.addEventListener('resize', updateBounds);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateBounds);
    };
  }, [isOpen, drawerWidth, drawerHeight]);
  const drawerStyle = useMemo(
    () => calculateDrawerStyle(drawerWidth, drawerHeight, isOpen),
    [drawerWidth, drawerHeight, isOpen]
  );

  // Drag controls
  const dragControls = useDragControls();

  // Create handlers using factory functions
  const voiceHandlers = createVoiceHandlers({
    startRecording,
    stopRecording,
    setIsTranscribing,
    setTranscriptionError,
    setInputValue,
  });

  const modalHandlers = createModalHandlers({
    setIsConfirmModalOpen,
    setIsFieldConfigModalOpen,
  });

  // Effects
  useEffect(() => {
    if (isLoading && !thinkingText && thinkingStatuses.length === 0) {
      setIsThinkingExpanded(false);
      setIsThinkingVisible(true);
    }
  }, [isLoading, thinkingText, thinkingStatuses]);

  useEffect(() => {
    if (selectedPlanDetail && !mounted) {
      setSearchMode("current");
      setMounted(true);
    }
  }, [selectedPlanDetail, mounted]);

  // Note: We do NOT automatically clear selectedSpacesChips when multiSelectArray becomes empty
  // because they are independent states:
  // - multiSelectArray: temporary selection from drag-select
  // - selectedSpacesChips: permanent user-saved selection
  // (This useEffect was removed to fix the bug where chips disappeared after clicking "Add Spaces")

  // Handlers
  const handleSetInputValue = (value: string) => {
    setInputValue(value);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim() || isLoading) return;
    const useGlobalMode = searchMode === "global";

    // Prepend space context if selected
    const messageToSend = spaceSelectedForAI
      ? `For space ${spaceSelectedForAI.spaceNumber} in plan ${spaceSelectedForAI.planName} of property ${spaceSelectedForAI.propertyName}: ${inputValue}`
      : inputValue;

    sendMessage(messageToSend, useGlobalMode, selectedSpacesChips, searchMode, isChartMode);
    setInputValue("");
    setShowSamples(false);
    setReplyTarget(null);

    // Clear the space context after sending
    if (spaceSelectedForAI) {
      setSpaceSelectedForAI(null);
    }
  };

  const handleStopQuery = () => {
    if (cancelQuery) {
      cancelQuery();
    }
  };

  const handleClearHistory = () => {
    clearHistoryMessagesToSend();
    modalHandlers.closeConfirmModal();
    clearCurrentHighlight();
    cadviewer.cvjs_clearSpaceLayer();
    // 9.27.3
    cadviewer.cvjs_hideOnlyPop();
  };

  const handleToggleChartMode = () => {
    setIsChartMode(!isChartMode);
  };

  const handleClearHighlight = () => {
    clearCurrentHighlight();
    cadviewer.cvjs_clearSpaceLayer();
    // 9.27.3
    cadviewer.cvjs_hideOnlyPop();
  };

  const handleScopeChange = (newScope: SearchMode) => {
    setSearchMode(newScope);
    setShowScopeMenu(false);
    handleClearHighlight();
    handleClearAllSpaceChips();
  };

  const handleDistanceTypeChange = (newType: DistanceType) => {
    setDistanceType(newType);
    setShowDistanceMenu(false);
  };

  const handleToggleScopeMenu = () => {
    setShowScopeMenu(!showScopeMenu);
    setShowDistanceMenu(false);
    setShowQuickActionsMenu(false);
  };

  const handleToggleDistanceMenu = () => {
    setShowDistanceMenu(!showDistanceMenu);
    setShowScopeMenu(false);
    setShowQuickActionsMenu(false);
  };

  const handleToggleQuickActionsMenu = () => {
    setShowQuickActionsMenu(!showQuickActionsMenu);
    setShowScopeMenu(false);
    setShowDistanceMenu(false);
  };

  // Highlight every space of a wing on the CAD plan (distinct blue), so the
  // highlighted plan can then be exported for the deal sheet.
  const highlightWingSpaces = (spaceNumbers: string[]) => {
    cadviewer.cvjs_clearSpaceLayer();
    spaceNumbers.forEach((raw) => {
      const normalized = String(raw).replaceAll(".", "_").replaceAll(" ", "_");
      cadviewer.cvjs_highlightSpace(normalized, highlight_blue);
    });
    cadviewer.cvjs_hideOnlyPop();
  };

  const triggerQuickAction = (actionName: string) => {
    setShowQuickActionsMenu(false);

    // AI-backed space-level insights — require exactly one selected space (one
    // tenant). The portfolio/property scope lives in the prompt, so we send in
    // global mode (no plan restriction) and let the agent return a table.
    if (actionName === "Tenant Ranking" || actionName === "Tenant Comparison") {
      if (selectedSpacesChips.length !== 1) {
        addLocalMessage?.(
          "Please select exactly one space (a single tenant) for this analysis."
        );
        return;
      }

      const spaceData = getSelectedSpacesData(plansInfo, selectedSpacesChips)[0];
      const ctx = spaceData
        ? getTenantContextForSpace(spaceData)
        : { tenantName: "", merchandiseCategory: "" };
      const tenantName = ctx.tenantName || selectedSpacesChips[0];

      if (!tenantName) {
        addLocalMessage?.(
          "Could not resolve the tenant for the selected space. Ensure a plan is loaded and try again."
        );
        return;
      }

      const prompt =
        actionName === "Tenant Ranking"
          ? buildTenantRankingPrompt(tenantName)
          : buildTenantComparisonPrompt(
              tenantName,
              ctx.merchandiseCategory,
              selectedPlan?.propertyName || ""
            );

      sendMessage(prompt, true, [], "global", false);
      return;
    }

    // Check if we have selected spaces
    if (selectedSpacesChips.length === 0) {
      if (addLocalMessage) {
        addLocalMessage(
          "Please select at least one space to perform this analysis. You can select spaces on the CAD canvas and click 'Add Selected Spaces'."
        );
      }
      return;
    }

    // Get space data from plansInfo
    const spacesData = getSelectedSpacesData(plansInfo, selectedSpacesChips);

    if (spacesData.length === 0) {
      if (addLocalMessage) {
        addLocalMessage(
          "Could not find data for the selected spaces. Please ensure a plan is loaded and try again."
        );
      }
      return;
    }

    let tableData = null;
    let messageContent = "";
    let isWingAnalysis = false;

    if (actionName === "Financial Summary") {
      tableData = calculateFinancialSummary(spacesData);
      messageContent = generateQuickActionMessage(
        "Financial Summary",
        spacesData.length,
        selectedPlan?.propertyName
      );
    } else if (actionName === "Opportunity Cost") {
      tableData = calculateOpportunityCost(spacesData);
      messageContent = generateQuickActionMessage(
        "Opportunity Cost",
        spacesData.length,
        selectedPlan?.propertyName
      );
    } else if (actionName === "Wing Analysis") {
      tableData = calculateWingAnalysis(spacesData);
      messageContent = generateQuickActionMessage(
        "Wing Analysis",
        spacesData.length,
        selectedPlan?.propertyName
      );
      isWingAnalysis = true;
    }

    if (tableData && addLocalMessage) {
      // Wing space numbers (from the actual matched data) for highlight + export.
      const wingSpaceNumbers = isWingAnalysis
        ? spacesData
            .map((s) => s.spaceNumber)
            .filter((n): n is string => !!n)
        : [];

      addLocalMessage(messageContent, {
        tables: tableData,
        query_interpretation: {
          mall_name: selectedPlan?.propertyName,
          plan_name: selectedPlanDetail?.planName,
        },
        ...(isWingAnalysis && {
          wingAnalysis: { spaceNumbers: wingSpaceNumbers },
        }),
      });

      // Immediately highlight the whole wing on the plan.
      if (isWingAnalysis && wingSpaceNumbers.length > 0) {
        highlightWingSpaces(wingSpaceNumbers);
      }
    }
  };

  // Rating handlers
  const handleRateMessage = useCallback((assistantMessage: any) => {
    console.log('[AIChatDrawer] handleRateMessage called', assistantMessage?.uuid, { searchMode, selectedPlan: selectedPlan?.propertyName, selectedPlanDetail: selectedPlanDetail?.planName });
    // Find the preceding user message
    const msgIndex = messages.findIndex((m: any) => m.uuid === assistantMessage.uuid);
    let userQuery = "";
    let propertyName = selectedPlan?.propertyName;
    let planName = selectedPlanDetail?.planName;
    let msgSearchMode = searchMode;

    if (msgIndex > 0) {
      const userMsg = messages[msgIndex - 1];
      if (userMsg.role === "user") {
        userQuery = userMsg.content;
        if (userMsg.metadata?.planInfo) {
          propertyName = userMsg.metadata.planInfo.propertyName || propertyName;
          planName = userMsg.metadata.planInfo.planName || planName;
        }
        if (userMsg.metadata?.searchMode) {
          msgSearchMode = userMsg.metadata.searchMode;
        }
      }
    }

    setRatingModalState({
      isOpen: true,
      assistantMessage,
      userQuery,
      propertyName,
      planName,
      searchMode: msgSearchMode,
    });
  }, [messages, selectedPlan, selectedPlanDetail, searchMode]);

  const handleSubmitRating = async (data: {
    rating: number;
    comment: string;
    userName?: string;
    userEmail?: string;
  }) => {
    console.log('[AIChatDrawer] handleSubmitRating called', { data, ratingModalState });
    if (!ratingModalState) {
      console.warn('[AIChatDrawer] ratingModalState is null, aborting submit');
      return;
    }

    setIsSubmittingRating(true);

    // Determine AI version from config
    let aiVersion = "V1";
    if (config.useAIChatV5) aiVersion = "V5";
    else if (config.useAIChatV4) aiVersion = "V4";
    else if (config.useAIChatV3) aiVersion = "V3";
    else if (config.useAIChatV2) aiVersion = "V2";

    // Find user message for metadata
    const msgIndex = messages.findIndex((m: any) => m.uuid === ratingModalState.assistantMessage.uuid);
    const userMsg = msgIndex > 0 ? messages[msgIndex - 1] : null;

    const assistantMsg: any = ratingModalState.assistantMessage;
    const isAnalyticsProvider =
      config.useAIChatV5 &&
      config.aiChatV5Provider === "cadviewer-analytics" &&
      !!assistantMsg.queryLogId;
    console.log('[AIChatDrawer] Rating branch decision', {
      useAIChatV5: config.useAIChatV5,
      provider: config.aiChatV5Provider,
      queryLogId: assistantMsg.queryLogId,
      isAnalyticsProvider,
    });

    if (isAnalyticsProvider) {
      const baseUrl = config.aiChatV5Endpoint
        ? config.aiChatV5Endpoint.replace(/\/ai\/proxy\/?$/, "")
        : config.ServerBackEndUrl || `${window.location.protocol}//${window.location.hostname}:3000`;

      const analyticsPayload = {
        stars: data.rating,
        comment: data.comment,
        endUserId: getOrCreateEndUserId(),
        analyticsTarget: config.analyticsTarget,
      };

      console.log('[AIChatDrawer] Submitting analytics rating to', baseUrl, assistantMsg.queryLogId, analyticsPayload);
      const result = await submitAnalyticsRating(baseUrl, assistantMsg.queryLogId, analyticsPayload);
      console.log('[AIChatDrawer] Analytics rating submission result:', result);

      setIsSubmittingRating(false);
      return;
    }

    const payload: AIRatingPayload = {
      messageUuid: ratingModalState.assistantMessage.uuid || "",
      rating: data.rating,
      comment: data.comment,
      userName: data.userName,
      userEmail: data.userEmail,
      userQuery: ratingModalState.userQuery,
      aiResponse: ratingModalState.assistantMessage.content || "",
      propertyName: ratingModalState.propertyName,
      planName: ratingModalState.planName,
      searchMode: userMsg?.metadata?.searchMode || ratingModalState.searchMode,
      selectedSpaces: userMsg?.metadata?.selectedSpaces,
      aiVersion,
      requestId: ratingModalState.assistantMessage.requestId,
      timestamp: new Date().toISOString(),
      queryTimestamp: userMsg?.metadata?.timestamp,
    };

    // Determine base URL for ratings endpoint
    const baseUrl = config.aiRatingEndpoint
      ? config.aiRatingEndpoint.replace(/\/ai\/ratings\/?$/, "")
      : config.ServerBackEndUrl || `${window.location.protocol}//${window.location.hostname}:3000`;

    console.log('[AIChatDrawer] Submitting rating to', baseUrl, payload);

    const result = await submitAIRating(baseUrl, payload);
    console.log('[AIChatDrawer] Rating submission result:', result);

    setIsSubmittingRating(false);
    // Modal shows success state internally, don't close immediately
  };

  const handleCloseRatingModal = useCallback(() => {
    setRatingModalState(null);
    setIsSubmittingRating(false);
  }, []);

  const handleVoiceRecordingClick = async () => {
    if (isVoiceRecording) {
      await voiceHandlers.handleStopVoiceRecording();
    } else {
      await voiceHandlers.handleStartVoiceRecording();
    }
  };

  // Handle select space for AI - toggles the blinking effect and waits for space selection
  const handleSelectSpaceForAI = useCallback(() => {
    if (isSelectingSpaceForAI) {
      // If already selecting, cancel the selection mode
      stopSelectingSpaceForAI();
    } else {
      // Clear any previous selection before starting new selection
      cadviewer.cvjs_clearSpaceLayer();
      // 9.27.3 - Also hide popups
      cadviewer.cvjs_hideOnlyPop();
      setSpaceSelectedForAI(null);
      setSelectedSpaceObjectID(undefined);
      // Enable the selecting mode (will trigger blinking effect on canvas)
      startSelectingSpaceForAI();
    }
  }, [isSelectingSpaceForAI, startSelectingSpaceForAI, stopSelectingSpaceForAI, setSpaceSelectedForAI, setSelectedSpaceObjectID]);

  // Handle drag select spaces - activates drag select mode and triggers the CADViewer drag select
  const handleDragSelectSpaces = useCallback(() => {
    // Start drag select mode (shows blinking effect)
    startDragSelect();
    // Trigger the actual drag select in CADViewer
    enableDragSelectSpaces();
  }, [startDragSelect, enableDragSelectSpaces]);

  // Clear the space context badge - also clears the global space selection
  const handleClearSpaceContext = useCallback(() => {
    // Clear the local store
    setSpaceSelectedForAI(null);

    // Clear the global space selection (same as SpaceObjectInfo close button)
    setSelectedSpaceObjectID(undefined);
    cadviewer.cvjs_clearSpaceLayer();
    // 9.27.3
    cadviewer.cvjs_hideOnlyPop();
  }, [setSpaceSelectedForAI, setSelectedSpaceObjectID]);

  // Content that is shared between pinned and floating modes
  const renderDrawerContent = () => (
    <>
      {/* Header */}
      <DrawerHeader
        onClose={() => {
          if (isPoppedOut) {
            closePopOut();
          }
          onClose();
        }}
        openConfirmModal={modalHandlers.openConfirmModal}
        openFieldConfigModal={modalHandlers.openFieldConfigModal}
        dragControls={dragControls}
        isPinned={isPinned}
        onTogglePin={onTogglePin}
        showSamples={showSamples}
        onToggleSamples={() => setShowSamples((s) => !s)}
        isPoppedOut={isPoppedOut}
        onTogglePopOut={config.enableAIAssistantPictureInPicture ? togglePopOut : undefined}
      />

      {/* Top Control Bar (Moved from bottom) */}
      <div className="bg-white border-b border-slate-200 relative z-20 shadow-sm">
        <TopControlBar
          searchMode={searchMode}
          distanceType={distanceType}
          selectedPlanDetail={selectedPlanDetail}
          selectedPlan={selectedPlan}
          showScopeMenu={showScopeMenu}
          showDistanceMenu={showDistanceMenu}
          onToggleScopeMenu={handleToggleScopeMenu}
          onToggleDistanceMenu={handleToggleDistanceMenu}
          onScopeChange={handleScopeChange}
          onDistanceTypeChange={handleDistanceTypeChange}
          onAddSelectedSpaces={handleAddSelectedSpaces}
          onDragSelectSpaces={handleDragSelectSpaces}
          onSelectSpaceForAI={handleSelectSpaceForAI}
          onClearHighlight={handleClearHighlight}
          isDragSelectActive={isDragSelectActive}
          isSelectingSpaceForAI={isSelectingSpaceForAI}
          showQuickActionsMenu={showQuickActionsMenu}
          onToggleQuickActionsMenu={handleToggleQuickActionsMenu}
          onTriggerQuickAction={triggerQuickAction}
          quickActionsConfig={config.quickActionsConfig}
          selectedSpacesChips={selectedSpacesChips}
          onRemoveSpaceChip={handleRemoveSpaceChip}
          onClearAllSpaceChips={handleClearAllSpaceChips}
          plansInfo={plansInfo}
        />
      </div>

      {/* Messages Area */}
      <MessagesArea
        messages={messages}
        config={config}
        currentHighlight={currentHighlight}
        internalCADViewerAICallback={internalCADViewerAICallback}
        onSetInputValue={handleSetInputValue}
        onRateMessage={config.enableAIRating ? handleRateMessage : undefined}
        onFollowUp={handleFollowUp}
        thinkingText={thinkingText}
        thinkingStatuses={thinkingStatuses}
        isThinkingVisible={isThinkingVisible}
        isThinkingExpanded={isThinkingExpanded}
        isLoading={isLoading}
        isSearching={isSearching}
        onToggleThinkingExpanded={() => setIsThinkingExpanded(!isThinkingExpanded)}
        onToggleThinkingVisible={() => setIsThinkingVisible(!isThinkingVisible)}
        showSamplesOverlay={showSamples}
        onCloseSamples={() => setShowSamples(false)}
      />

      {/* Bottom Controls Area */}
      <div className="bg-white border-t border-slate-200 relative">
        {/* Space Context Badge - shown when a space is selected */}
        {spaceSelectedForAI && (
          <div className="px-3 py-1.5 bg-teal-50 border-b border-teal-100">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-teal-600 flex-shrink-0" />
              <span className="text-xs text-teal-700 flex-1 truncate">
                <span className="font-medium">Context:</span> Space {spaceSelectedForAI.spaceNumber} in {spaceSelectedForAI.planName}
              </span>
              <button
                onClick={handleClearSpaceContext}
                className="p-0.5 rounded hover:bg-teal-100 text-teal-500 hover:text-teal-700 transition-colors"
                title="Remove context"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Reply quote (WhatsApp-style) — shown when following up on an answer */}
        {replyTarget && (
          <div className="px-3 py-1.5 bg-orange-50 border-b border-orange-100">
            <div className="flex items-start gap-2">
              <div className="w-0.5 self-stretch bg-orange-400 rounded-full flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-orange-700">
                  Following up on
                </div>
                <div className="text-xs text-slate-600 line-clamp-2">
                  {replyTarget.content}
                </div>
              </div>
              <button
                onClick={() => setReplyTarget(null)}
                className="p-0.5 rounded hover:bg-orange-100 text-orange-500 hover:text-orange-700 transition-colors flex-shrink-0"
                title="Cancel reply"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Input Area */}
        <InputArea
          inputRef={inputRef}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSendMessage={handleSendMessage}
          onStopQuery={handleStopQuery}
          isLoading={isLoading}
          isSearching={isSearching}
          isVoiceMode={isVoiceMode}
          isVoiceRecording={isVoiceRecording}
          isTranscribing={isTranscribing}
          isChartMode={isChartMode}
          onToggleChartMode={handleToggleChartMode}
          onVoiceRecordingClick={handleVoiceRecordingClick}
          error={error}
          clearError={clearError}
          transcriptionError={transcriptionError}
          voiceRecorderError={voiceRecorderError}
        />



        {/* Footer Status */}
        <FooterStatus
          searchMode={searchMode}
          selectedPlan={selectedPlan}
          selectedPlanDetail={selectedPlanDetail}
          isLoading={isLoading}
          isVoiceRecording={isVoiceRecording}
          isTranscribing={isTranscribing}
          transcriptionError={transcriptionError}
          voiceRecorderError={voiceRecorderError}
        />
      </div>
    </>
  );

  // If popped out to external window
  if (isPoppedOut && popOutWindow) {
    return (
      <>
        <WindowPortal targetWindow={popOutWindow}>
          <div className="flex flex-col w-full h-full bg-white overflow-hidden relative z-0">
            {renderDrawerContent()}
          </div>
        </WindowPortal>

        {/* Modals */}
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onClose={modalHandlers.closeConfirmModal}
          onConfirm={handleClearHistory}
          title="Clear history"
          message="Are you sure you want to clear the history of messages to send? This action is irreversible."
          confirmText="Clear"
          cancelText="Cancel"
          container={popOutWindow.document.body}
        />

        <FieldConfigModal
          isOpen={isFieldConfigModalOpen}
          onClose={modalHandlers.closeFieldConfigModal}
          container={popOutWindow.document.body}
        />

        {ratingModalState && (
          <RatingModal
            isOpen={ratingModalState.isOpen}
            onClose={handleCloseRatingModal}
            onSubmit={handleSubmitRating}
            userQuery={ratingModalState.userQuery}
            propertyName={ratingModalState.propertyName}
            planName={ratingModalState.planName}
            searchMode={ratingModalState.searchMode}
            isSubmitting={isSubmittingRating}
            container={popOutWindow.document.body}
          />
        )}
      </>
    );
  }

  // Pinned mode: render as a simple flex container
  if (isPinned) {
    return (
      <>
        <div
          className="flex flex-col h-full bg-white border-l border-slate-200"
          aria-modal="true"
          role="dialog"
        >
          {renderDrawerContent()}
        </div>

        {/* Modals */}
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onClose={modalHandlers.closeConfirmModal}
          onConfirm={handleClearHistory}
          title="Clear history"
          message="Are you sure you want to clear the history of messages to send? This action is irreversible."
          confirmText="Clear"
          cancelText="Cancel"
        />

        <FieldConfigModal
          isOpen={isFieldConfigModalOpen}
          onClose={modalHandlers.closeFieldConfigModal}
        />

        {ratingModalState && (
          <RatingModal
            isOpen={ratingModalState.isOpen}
            onClose={handleCloseRatingModal}
            onSubmit={handleSubmitRating}
            userQuery={ratingModalState.userQuery}
            propertyName={ratingModalState.propertyName}
            planName={ratingModalState.planName}
            searchMode={ratingModalState.searchMode}
            isSubmitting={isSubmittingRating}
          />
        )}
      </>
    );
  }

  // Floating mode: render with motion.div for drag/resize
  return (
    <>
      <motion.div
        ref={drawerRef}
        drag
        dragListener={false}
        dragControls={dragControls}
        onDragStart={() => EventSystem.publish("cadviewer.is_dragging_pane", true)}
        onDragEnd={() => EventSystem.publish("cadviewer.is_dragging_pane", false)}
        dragConstraints={dynamicConstraints}
        dragElastic={0}
        dragMomentum={false}
        className="border border-slate-200"
        style={{
          ...drawerStyle,
          x,
          y,
        }}
        animate={{
          x: isOpen ? undefined : drawerWidth + 32,
          y: isOpen ? undefined : 0,
        }}
        transition={{
          type: "spring",
          damping: 25,
          stiffness: 300,
        }}
        aria-modal="true"
        role="dialog"
      >
        {/* 8-Direction Resize Handles */}
        <div // Top (n)
          onMouseDown={(e) => onResizeStart('n', e)}
          style={{ position: "absolute", left: 8, right: 8, top: -4, height: 8, cursor: "ns-resize", zIndex: 50 }}
        />
        <div // Bottom (s)
          onMouseDown={(e) => onResizeStart('s', e)}
          style={{ position: "absolute", left: 8, right: 8, bottom: -4, height: 8, cursor: "ns-resize", zIndex: 50 }}
        />
        <div // Right (e)
          onMouseDown={(e) => onResizeStart('e', e)}
          style={{ position: "absolute", top: 8, bottom: 8, right: -4, width: 8, cursor: "ew-resize", zIndex: 50 }}
        />
        <div // Left (w)
          onMouseDown={(e) => onResizeStart('w', e)}
          style={{ position: "absolute", top: 8, bottom: 8, left: -4, width: 8, cursor: "ew-resize", zIndex: 50 }}
        />
        <div // Top-Right (ne)
          onMouseDown={(e) => onResizeStart('ne', e)}
          style={{ position: "absolute", top: -4, right: -4, width: 12, height: 12, cursor: "nesw-resize", zIndex: 51 }}
        />
        <div // Top-Left (nw)
          onMouseDown={(e) => onResizeStart('nw', e)}
          style={{ position: "absolute", top: -4, left: -4, width: 12, height: 12, cursor: "nwse-resize", zIndex: 51 }}
        />
        <div // Bottom-Right (se)
          onMouseDown={(e) => onResizeStart('se', e)}
          style={{ position: "absolute", bottom: -4, right: -4, width: 12, height: 12, cursor: "nwse-resize", zIndex: 51 }}
        />
        <div // Bottom-Left (sw)
          onMouseDown={(e) => onResizeStart('sw', e)}
          style={{ position: "absolute", bottom: -4, left: -4, width: 12, height: 12, cursor: "nesw-resize", zIndex: 51 }}
        />

        {renderDrawerContent()}
      </motion.div>


      {/* Modals */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={modalHandlers.closeConfirmModal}
        onConfirm={handleClearHistory}
        title="Clear history"
        message="Are you sure you want to clear the history of messages to send? This action is irreversible."
        confirmText="Clear"
        cancelText="Cancel"
      />

      <FieldConfigModal
        isOpen={isFieldConfigModalOpen}
        onClose={modalHandlers.closeFieldConfigModal}
      />

      {ratingModalState && (
        <RatingModal
          isOpen={ratingModalState.isOpen}
          onClose={handleCloseRatingModal}
          onSubmit={handleSubmitRating}
          userQuery={ratingModalState.userQuery}
          propertyName={ratingModalState.propertyName}
          planName={ratingModalState.planName}
          isSubmitting={isSubmittingRating}
        />
      )}
    </>
  );
};

export default AIChatDrawer;
